// src/pages/TCC/TCCPage.jsx

import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs,
  Link,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ManageSearch as ManageSearchIcon,
  FindInPage as FindInPageIcon,
  Psychology as PsychologyIcon,
  Science as ScienceIcon,
  Gavel as GavelIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  BarChart as BarChartIcon,
  CalendarMonth as CalendarMonthIcon,
  Shield as ShieldIcon,
  Policy as PolicyIcon,
  Security as SecurityIcon,
  School as SchoolIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const FeatureCard = ({ icon, title, text }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <Paper sx={{ p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon}
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Typography color="text.secondary">{text}</Typography>
    </Paper>
  </motion.div>
);

FeatureCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

const StatCard = ({ icon, value, label }) => (
  <Card elevation={2} sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon}
        <Typography variant="h4">{value}</Typography>
      </Box>
      <Typography color="text.secondary">{label}</Typography>
    </CardContent>
  </Card>
);

StatCard.propTypes = {
  icon: PropTypes.node.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
};

const TCCPage = () => {
  const steps = [
    { label: 'Proposta', icon: <ManageSearchIcon /> },
    { label: 'Orientação', icon: <SchoolIcon /> },
    { label: 'Similaridade & IA', icon: <FindInPageIcon /> },
    { label: 'Revisão', icon: <ScienceIcon /> },
    { label: 'Defesa', icon: <GavelIcon /> },
    { label: 'Certificação', icon: <CheckCircleOutlineIcon /> },
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          color: 'primary.contrastText',
          background:
            'linear-gradient(135deg, rgba(5,10,25,1) 0%, rgba(8,60,100,1) 60%)',
          py: { xs: 8, md: 12 },
          mb: 6,
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Breadcrumbs sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>
            <Link component={RouterLink} to="/" color="inherit" underline="hover">
              Início
            </Link>
            <Typography color="inherit">TCC</Typography>
          </Breadcrumbs>

          <Typography variant="h2" component="h1" gutterBottom>
            Módulo TCC
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 900, opacity: 0.9 }}>
            Fluxo completo do Trabalho de Conclusão de Curso — da proposta à defesa —
            com verificações de similaridade, detecção de sinais de IA e trilhas de auditoria.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: 'wrap' }}>
            <Chip
              icon={<VerifiedIcon />}
              label="ZenKroll — Produto da DataBase Lda"
              color="default"
              variant="outlined"
            />
            <Chip icon={<ShieldIcon />} label="Integridade e Conformidade" variant="outlined" />
            <Chip icon={<PolicyIcon />} label="Políticas e Limiar por Curso" variant="outlined" />
          </Stack>

          <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" color="secondary" component={RouterLink} to="/leads">
              Solicitar Demonstração
            </Button>
            <Button variant="outlined" color="inherit" component={RouterLink} to="/integridade">
              Ver Relatórios de Integridade
            </Button>
          </Box>
        </Container>

        {/* Decor */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.2,
            backgroundImage:
              'radial-gradient(circle at 20% 10%, rgba(255,200,50,.6) 0 10%, transparent 40%), radial-gradient(circle at 80% 0%, rgba(0,255,220,.4) 0 8%, transparent 30%)',
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ flex: 1 }}>
        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<CalendarMonthIcon color="primary" />}
              value="6 passos"
              label="Ciclo do TCC estruturado"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<BarChartIcon color="primary" />} value="95%" label="Satisfação reportada" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<SecurityIcon color="primary" />} value="100%" label="Ações auditáveis" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<PolicyIcon color="primary" />} value="Flexível" label="Políticas por curso/turma" />
          </Grid>
        </Grid>

        {/* Stepper */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 6 }}>
          <Typography variant="h5" gutterBottom>
            Fluxo do TCC
          </Typography>
          <Stepper alternativeLabel sx={{ mt: 2 }}>
            {steps.map((s) => (
              <Step key={s.label} active>
                <StepLabel icon={s.icon}>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Features principais */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <FeatureCard
              icon={<ManageSearchIcon color="primary" />}
              title="Submissão & Versionamento"
              text="Proposta e monografia com controlo de versões, metadados, requisitos e validações automáticas."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FeatureCard
              icon={<FindInPageIcon color="secondary" />}
              title="Similaridade & Fontes"
              text="Comparação com bases institucionais e web pública; percentuais por fonte, trechos e ligações."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FeatureCard
              icon={<PsychologyIcon color="success" />}
              title="Detecção de IA (ensemble)"
              text="Classificadores combinados e padrões linguísticos; parâmetros por instituição e idioma."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FeatureCard
              icon={<ScienceIcon color="info" />}
              title="Evidências & Explicabilidade"
              text="Realce de trechos, pesos por evidência, histórico de versões e anexos técnicos."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FeatureCard
              icon={<GavelIcon color="warning" />}
              title="Juris & Defesa"
              text="Formação de Juris, agenda de defesas, actas digitais e emissão de credenciais."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FeatureCard
              icon={<BarChartIcon color="action" />}
              title="Métricas & Conformidade"
              text="Aprovação, prazos e desempenho por curso/instituição; políticas e limiares aplicados."
            />
          </Grid>
        </Grid>

        {/* Blocos por perfil */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Para a Instituição
              </Typography>
              <Typography color="text.secondary">
                Padrões, auditoria, indicadores e exportações oficiais (PDF/Excel). Perfis e permissões
                ajustáveis, com políticas por curso, turma e período.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Para Orientadores e Juri
              </Typography>
              <Typography color="text.secondary">
                Pareceres estruturados, rubricas, actas digitais e histórico de decisões com carimbo temporal.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Para Estudantes
              </Typography>
              <Typography color="text.secondary">
                Submissão clara, feedback transparente e acompanhamento do progresso até à defesa e certificação.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* FAQ */}
        <Typography variant="h5" gutterBottom>
          Perguntas Frequentes
        </Typography>
        <Grid container spacing={2} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Como são aplicadas as políticas e limiares?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">
                  As políticas podem ser definidas por instituição/curso/turma e aplicadas por estado
                  (rascunho, versão final, reentrega). Os limiares influenciam a sinalização de similaridade e IA.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>As decisões ficam auditáveis?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">
                  Sim. Toda acção relevante gera registo com utilizador, carimbo temporal e contexto — garantindo
                  rastreabilidade e confiança nas decisões académicas.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} md={6}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Como funciona o agendamento de defesas?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">
                  A instituição define janelas, forma juris e confirma horários. O sistema gere convites, actas e
                  emissão de declarações/certidões após a conclusão.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>O ZenKroll é um produto da DataBase Lda?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">
                  Sim. O ZenKroll é desenvolvido e mantido pela <strong>DataBase Lda</strong>, assegurando qualidade,
                  segurança e evolução contínua.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>

        {/* CTA */}
        <Paper sx={{ p: { xs: 3, md: 4 }, mb: 8, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Eleva o teu processo de TCC com integridade e eficiência
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Integração com políticas institucionais, auditoria completa e relatórios explicáveis.
          </Typography>
          <Stack direction="row" justifyContent="center" spacing={2} flexWrap="wrap">
            <Button variant="contained" color="primary" component={RouterLink} to="/leads">
              Solicitar Demonstração
            </Button>
            <Button variant="outlined" color="primary" component={RouterLink} to="/integridade">
              Explorar Integridade
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default TCCPage;
